import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import type { BonusOrdinalRatingEnum } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Captain } from '@sorare/core/src/components/so5/Captain';
import { isType } from '@sorare/core/src/lib/gql';

import { rgbColorPerBonusRating } from '#src/lib/bonus.ts';

import { BonusOrPointsChip } from './BonusOrPointsChip/index.tsx';
import type { AppearanceAvatar_appearanceInterface } from './__generated__/index.graphql.ts';

const StyledVertical = styled(Vertical)`
  align-items: center;
`;
const AvatarWrapper = styled.div`
  position: relative;
  border-radius: var(--unit);
  border: 2px solid transparent;

  &.selected {
    border-color: var(--c-blue-400);
  }
`;
const CaptainWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--unit));
  left: calc(-1 * var(--unit));
`;
const Avatar = styled.img`
  width: 100%;
  aspect-ratio: 1;
  border-radius: var(--unit);
  object-fit: cover;
`;

type Props = {
  appearance: AppearanceAvatar_appearanceInterface;
  bonusRating?: BonusOrdinalRatingEnum | null;
  selected: boolean;
  onClick: () => void;
};

export const AppearanceAvatar = ({
  appearance,
  bonusRating,
  selected,
  onClick,
}: Props) => {
  const { avatarUrl, captain, powerBreakdownOrPowerPointsBreakdown } =
    appearance;

  return (
    <ButtonBase onClick={onClick}>
      <StyledVertical>
        <AvatarWrapper className={classNames({ selected, captain })}>
          {!!appearance.captain && (
            <CaptainWrapper>
              <Captain active size={2} />
            </CaptainWrapper>
          )}
          {!!avatarUrl && <Avatar src={avatarUrl} alt="" />}
        </AvatarWrapper>
        <BonusOrPointsChip
          rgbColor={
            bonusRating
              ? rgbColorPerBonusRating[bonusRating]
              : 'var(--c-rgb-set-surface-primary-text)'
          }
          hasUpcomingGame={!!appearance.upcomingGame}
          bonusBasisPoints={
            isType(powerBreakdownOrPowerPointsBreakdown, 'PowerBreakdown')
              ? powerBreakdownOrPowerPointsBreakdown.collectionBasisPoints +
                powerBreakdownOrPowerPointsBreakdown.seasonBasisPoints +
                powerBreakdownOrPowerPointsBreakdown.xpBasisPoints +
                powerBreakdownOrPowerPointsBreakdown.captainBasisPoints +
                powerBreakdownOrPowerPointsBreakdown.scarcityBasisPoints +
                powerBreakdownOrPowerPointsBreakdown.specialEditionCardsBasisPoints
              : null
          }
          points={
            isType(powerBreakdownOrPowerPointsBreakdown, 'PowerPointsBreakdown')
              ? powerBreakdownOrPowerPointsBreakdown.collectionPoints +
                powerBreakdownOrPowerPointsBreakdown.seasonPoints +
                powerBreakdownOrPowerPointsBreakdown.xpPoints +
                powerBreakdownOrPowerPointsBreakdown.captainPoints +
                powerBreakdownOrPowerPointsBreakdown.scarcityPoints +
                powerBreakdownOrPowerPointsBreakdown.specialEditionCardsPoints
              : null
          }
        />
      </StyledVertical>
    </ButtonBase>
  );
};

AppearanceAvatar.fragments = {
  appearanceInterface: gql`
    fragment AppearanceAvatar_appearanceInterface on AppearanceInterface {
      id
      captain
      upcomingGame {
        id
      }
      avatarUrl: pictureUrl(derivative: "avatar")
      powerBreakdownOrPowerPointsBreakdown {
        ... on PowerBreakdown {
          seasonBasisPoints
          collectionBasisPoints
          xpBasisPoints
          captainBasisPoints
          scarcityBasisPoints
          specialEditionCardsBasisPoints
        }
        ... on PowerPointsBreakdown {
          collectionPoints
          seasonPoints
          xpPoints
          captainPoints
          scarcityPoints
          specialEditionCardsPoints
        }
      }
    }
  ` as TypedDocumentNode<AppearanceAvatar_appearanceInterface>,
};
